import {
  Box,
  Divider,
  FormControl,
  FormLabel,
  Flex,
  Hide,
  Show,
  Text,
  Tooltip,
  Input,
  Switch,
  Stack,
  Textarea,
  Grid,
  GridItem,
  Collapse,
  Link,
  Heading,
  HStack,
  Button,
} from '@chakra-ui/react';
import { useContext, useMemo, useState, useEffect } from 'react';
import PassDownloadTemplateContext from '../../../store/client/PassDownloadTemplateContext';
import { useColorModeValue } from '@chakra-ui/react';
import LogoImage from '../LogoImage';
import FaviconImage from '../FaviconImage';
import { CustomExclamationMarkInverseIcon } from '../../../theme/icons/CustomExclamationMarkInverseIcon';
import CustomColorPicker from '../pass-templates/CustomColorPicker';
import PassTargetSection from './PassTargetSection';
import { CustomPlusIcon } from '../../../theme/icons/CustomPlusIcon';
import { CustomEditIcon } from '../../../theme/icons/CustomEditIcon';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import AddLanguageModal from '../../modals/AddLanguageModal';
import RemoveLanguageModal from '../../modals/RemoveLanguageModal';
import { downloadPageTranslations } from '../../../utils/downloadPageTranslations';
import { MeContext } from '../../../context/MeContext';

const PassDownloadTemplateDetails = ({
  isEdit,
  defaultEntityPage,
  setDefaultEntityPage,
  editingLang,
  setEditingLang,
  selectedPassTemplates,
  setSelectedPassTemplates,
  forcedOpenLanguages,
  translationErrors,
  setTranslationErrors,
  getActiveTranslationFields,
  showHelpAndFAQs,
  setShowHelpAndFAQs,
}) => {
  const meCtx = useContext(MeContext);
  const languagesSectionEnabled = meCtx.state.languagesSectionEnabled;
  const passDownloadCtx = useContext(PassDownloadTemplateContext);
  const modeFormControlBg = useColorModeValue('white', '');
  const modeLabelBg = useColorModeValue('#EFEFEF', 'primaryDark03');
  const modeHelperText = useColorModeValue('secondaryDark08', '#DDDDDD');
  const modeFormBoxBg = useColorModeValue('gradient', 'secondaryDark');
  const modeTextBg = useColorModeValue('quaternaryBackground', 'primaryDark06');
  const modeBorderColor = useColorModeValue('primaryBackground', 'primaryDark');
  const bgTooltip = useColorModeValue('secondaryDark', '#dddddd');
  const textTooltip = useColorModeValue('#ffffff', 'primaryDark');
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [isAddLanguageModalOpen, setIsAddLanguageModalOpen] = useState(false);
  const [isRemoveLanguageModalOpen, setIsRemoveLanguageModalOpen] =
    useState(false);

  const languageOptions = useMemo(() => {
    const displayNames = new Intl.DisplayNames(['en'], {
      type: 'language',
    });
    const availableLangCodes =
      downloadPageTranslations.SUPPORTED_LANGUAGE_CODES.filter(
        (code) => !Object.keys(passDownloadCtx.translations).includes(code)
      );
    return availableLangCodes
      .map((code) => ({
        label: displayNames.of(code),
        value: code,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [passDownloadCtx.translations]);

  const langCodes = useMemo(() => {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'language' });
    return Object.keys(passDownloadCtx.translations).sort((a, b) =>
      displayNames.of(a).localeCompare(displayNames.of(b))
    );
  }, [passDownloadCtx.translations]);

  useEffect(() => {
    if (!isEdit) return;
    if (!passDownloadCtx.clientHelpLink?.url) return;

    setShowHelpAndFAQs(true);
  }, [isEdit, passDownloadCtx.clientHelpLink?.url]);

  return (
    <Box role="form" className="autofillForDarkBg" borderRadius="15px">
      {!passDownloadCtx?.defaultForOrg && (
        <PassTargetSection
          isEdit={isEdit}
          defaultEntityPage={defaultEntityPage}
          setDefaultEntityPage={setDefaultEntityPage}
          selectedPassTemplates={selectedPassTemplates}
          setSelectedPassTemplates={setSelectedPassTemplates}
        />
      )}
      <Box
        mt={!passDownloadCtx?.defaultForOrg ? '20px' : '0'}
        className="autofillForDarkBg"
        borderRadius="15px"
        border="0.5px solid #D9D9D9"
        boxShadow="0px 5px 10px rgba(0, 0, 0, 0.05)"
        overflow="hidden"
        w="full"
        onClick={() => setEditingLang(null)}
      >
        <Box bg={modeFormBoxBg} w="full">
          <Stack
            p="14px 14px 14px 24px"
            bg={modeTextBg}
            direction="row"
            align="center"
          >
            <Flex direction="row" gap={1} align="center">
              <Text fontSize="18px" textStyle="headingFamilyMedium">
                Page text
              </Text>
            </Flex>
          </Stack>
          <Divider borderColor={modeBorderColor} />
          <FormControl bg={modeFormControlBg} isRequired>
            <Flex alignItems="center">
              <Hide below="2xl">
                <Box pl="24px" flexShrink={0} w="240px">
                  <FormLabel m={0}>Page name</FormLabel>
                </Box>
              </Hide>
              <Box p="14px" w="full" bg={modeLabelBg}>
                <Show below="2xl">
                  <FormLabel>Page name</FormLabel>
                </Show>
                <Input
                  value={passDownloadCtx.name}
                  onChange={(e) => passDownloadCtx.updateName(e.target.value)}
                  variant="filledForDarkBg"
                  id="pageName"
                  type="text"
                  placeholder="Type page name here..."
                />
              </Box>
            </Flex>
          </FormControl>
          <Divider />
          <FormControl bg={modeFormControlBg}>
            <Flex alignItems="center">
              <Hide below="2xl">
                <Box pl="24px" flexShrink={0} w="240px">
                  <FormLabel m={0}>Header text</FormLabel>
                </Box>
              </Hide>
              <Box p="14px" w="full" bg={modeLabelBg}>
                <Show below="2xl">
                  <FormLabel>Header text</FormLabel>
                </Show>
                <Input
                  value={passDownloadCtx.headerText}
                  onChange={(e) =>
                    passDownloadCtx.updateHeaderText(e.target.value)
                  }
                  variant="filledForDarkBg"
                  id="headerText"
                  type="text"
                  placeholder="Type header text here..."
                />
              </Box>
            </Flex>
          </FormControl>
          <Divider />
          <FormControl bg={modeFormControlBg}>
            <Flex alignItems="center">
              <Hide below="2xl">
                <Box pl="24px" flexShrink={0} w="240px">
                  <Flex direction="row" align="center" gap={1} my={0.5}>
                    <FormLabel m={0}>Body text</FormLabel>
                    <Tooltip
                      label="This is additional text that appears within the card on the screen"
                      hasArrow
                      placement="top"
                      fontSize="12px"
                      bg={bgTooltip}
                      color={textTooltip}
                      w="205px"
                      borderRadius="6px"
                      textAlign="center"
                      p="10px"
                    >
                      <Box
                        data-testid="body-text-tooltip-button"
                        mb={1}
                        cursor="pointer"
                      >
                        <CustomExclamationMarkInverseIcon
                          boxSize="12px"
                          mode={'light'}
                        />
                      </Box>
                    </Tooltip>
                  </Flex>
                </Box>
              </Hide>
              <Box p="14px" w="full" bg={modeLabelBg}>
                <Show below="2xl">
                  <Flex direction="row" align="center" gap={1} my={0.5}>
                    <FormLabel m={0}>Body text</FormLabel>
                    <Tooltip
                      label="This is additional text that appears within the card on the screen"
                      hasArrow
                      placement="top"
                      fontSize="12px"
                      bg={bgTooltip}
                      color={textTooltip}
                      w="205px"
                      borderRadius="6px"
                      textAlign="center"
                      p="10px"
                    >
                      <Box
                        data-testid="body-text-tooltip-button"
                        mb={1}
                        cursor="pointer"
                      >
                        <CustomExclamationMarkInverseIcon
                          boxSize="12px"
                          mode={'light'}
                        />
                      </Box>
                    </Tooltip>
                  </Flex>
                </Show>
                <Input
                  value={passDownloadCtx.passDownloadText}
                  onChange={(e) =>
                    passDownloadCtx.updatePassDownloadText(e.target.value)
                  }
                  variant="filledForDarkBg"
                  id="bodyText"
                  type="text"
                  placeholder="Type body text here..."
                />
              </Box>
            </Flex>
          </FormControl>
          <Divider />
          <FormControl bg={modeFormControlBg} isRequired>
            <Flex alignItems="center">
              <Hide below="2xl">
                <Box pl="24px" flexShrink={0} w="240px">
                  <Flex direction="row" align="center" gap={1} my={0.5}>
                    <FormLabel m={0}>Success screen message</FormLabel>
                    <Tooltip
                      label="Message displayed on the success screen after a pass is downloaded"
                      hasArrow
                      placement="top"
                      fontSize="12px"
                      bg={bgTooltip}
                      color={textTooltip}
                      w="205px"
                      borderRadius="6px"
                      textAlign="center"
                      p="10px"
                    >
                      <Box
                        data-testid="success-message-tooltip-button"
                        mb={1}
                        cursor="pointer"
                      >
                        <CustomExclamationMarkInverseIcon
                          boxSize="12px"
                          mode={'light'}
                        />
                      </Box>
                    </Tooltip>
                  </Flex>
                </Box>
              </Hide>
              <Box p="14px" w="full" bg={modeLabelBg}>
                <Show below="2xl">
                  <Flex direction="row" align="center" gap={1} my={0.5}>
                    <FormLabel m={0}>Success screen message</FormLabel>
                    <Tooltip
                      label="Message displayed on the success screen after a pass is downloaded"
                      hasArrow
                      placement="top"
                      fontSize="12px"
                      bg={bgTooltip}
                      color={textTooltip}
                      w="205px"
                      borderRadius="6px"
                      textAlign="center"
                      p="10px"
                    >
                      <Box
                        data-testid="success-message-tooltip-button"
                        mb={1}
                        cursor="pointer"
                      >
                        <CustomExclamationMarkInverseIcon
                          boxSize="12px"
                          mode={'light'}
                        />
                      </Box>
                    </Tooltip>
                  </Flex>
                </Show>
                <Flex direction="row" gap={3} alignItems="center" py={1.5}>
                  <Textarea
                    id="successMessage"
                    value={passDownloadCtx.successMessage}
                    onChange={(e) =>
                      passDownloadCtx.updateSuccessMessage(e.target.value)
                    }
                    type="text"
                    placeholder="Type success message here..."
                  />
                </Flex>
              </Box>
            </Flex>
          </FormControl>
          <Divider borderColor={modeBorderColor} />
          <FormControl isRequired bg={modeFormControlBg}>
            <Flex alignItems="center">
              <Hide below="2xl">
                <Box pl="24px" flexShrink={0} w="240px">
                  <FormLabel m={0}>Browser tab text</FormLabel>
                </Box>
              </Hide>
              <Box p="14px" w="full" bg={modeLabelBg}>
                <Show below="2xl">
                  <FormLabel>Browser tab text</FormLabel>
                </Show>
                <Input
                  value={passDownloadCtx.pageTitle}
                  onChange={(e) =>
                    passDownloadCtx.updatePageTitle(e.target.value)
                  }
                  variant="filledForDarkBg"
                  id="pageTitle"
                  type="text"
                  placeholder="Type browser tab text here..."
                />
              </Box>
            </Flex>
          </FormControl>
          <Divider />
        </Box>
      </Box>
      {languagesSectionEnabled && (
        <Box
          className="autofillForDarkBg"
          borderRadius="15px"
          border="0.5px solid #D9D9D9"
          boxShadow="0px 5px 10px rgba(0, 0, 0, 0.05)"
          w="full"
          mt="20px"
          overflow="hidden"
        >
          <Box bg={modeFormBoxBg} w="full" borderRadius="15px">
            <Stack
              p="14px 14px 14px 24px"
              bg={modeTextBg}
              direction="row"
              align="center"
              borderRadius={langCodes.length === 0 ? '15px' : '15px 15px 0 0'}
            >
              <Flex
                direction="row"
                align="center"
                justifyContent="space-between"
                w="full"
              >
                <Flex direction="column" gap={1}>
                  <Text fontSize="18px" textStyle="headingFamilyMedium">
                    Languages
                  </Text>
                  <Text
                    fontSize="12px"
                    textStyle="bodyFamilyRegular"
                    color={modeHelperText}
                  >
                    You can add up to 5 languages
                  </Text>
                </Flex>
                <Button
                  size="xs"
                  onClick={() => {
                    setEditingLang(null);
                    setSelectedLanguage(null);
                    setIsAddLanguageModalOpen(true);
                  }}
                  px={5}
                  isDisabled={langCodes.length === 5}
                >
                  <Flex alignItems="center" gap={1}>
                    <CustomPlusIcon boxSize="12px" />
                    <Text>Add</Text>
                  </Flex>
                </Button>
              </Flex>
            </Stack>
            <Divider borderColor={modeBorderColor} />
            <Collapse in={langCodes.length > 0}>
              {langCodes.map((lang, index) => (
                <>
                  {editingLang !== lang &&
                  !forcedOpenLanguages.includes(lang) ? (
                    <>
                      <FormControl
                        bg={modeFormControlBg}
                        borderRadius={
                          index === langCodes.length - 1 ? '15px' : '0'
                        }
                      >
                        <Flex alignItems="center">
                          <Hide below="2xl">
                            <Box
                              pl="24px"
                              flexShrink={0}
                              w="240px"
                              borderRadius={
                                index === langCodes.length - 1 ? '15px' : '0'
                              }
                            >
                              <FormLabel m={0}>
                                {new Intl.DisplayNames(['en'], {
                                  type: 'language',
                                }).of(lang)}
                              </FormLabel>
                            </Box>
                          </Hide>
                          <Box
                            w="full"
                            p="5px"
                            bg={modeLabelBg}
                            borderRadius={
                              index === langCodes.length - 1
                                ? { base: '0 0 15px 15px', '2xl': '0 0 15px 0' }
                                : '0'
                            }
                          >
                            <Show below="2xl">
                              <FormLabel pl="10px" pt="10px">
                                {new Intl.DisplayNames(['en'], {
                                  type: 'language',
                                }).of(lang)}
                              </FormLabel>
                            </Show>
                            <Flex
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              onClick={() => setEditingLang(lang)}
                              cursor="pointer"
                              pl={3}
                              pr={6}
                              py={2}
                            >
                              <>
                                <Box
                                  as={Link}
                                  onClick={() => setEditingLang(lang)}
                                  cursor="pointer"
                                >
                                  <HStack zIndex={1} padding="10px">
                                    <>
                                      <CustomEditIcon
                                        color="brand"
                                        boxSize="14px"
                                      />
                                      <Heading
                                        color="secondaryDark"
                                        fontSize="14px"
                                        top="1px"
                                        pos="relative"
                                      >
                                        Edit
                                      </Heading>
                                    </>
                                  </HStack>
                                </Box>
                                <ChevronDownIcon
                                  color="secondaryDark"
                                  boxSize="20px"
                                />
                              </>
                            </Flex>
                          </Box>
                        </Flex>
                      </FormControl>
                      <Divider borderColor={modeBorderColor} />
                    </>
                  ) : (
                    <>
                      <FormControl bg={modeFormControlBg}>
                        <Flex alignItems="center">
                          <Hide below="2xl">
                            <Box pl="24px" flexShrink={0} w="240px">
                              <FormLabel m={0}>
                                {new Intl.DisplayNames(['en'], {
                                  type: 'language',
                                }).of(lang)}
                              </FormLabel>
                            </Box>
                          </Hide>
                          <Box p="14px 14px" w="full" bg={modeLabelBg}>
                            <Flex
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              onClick={() => setEditingLang(null)}
                              cursor="pointer"
                              pr={3}
                              py={1.5}
                            >
                              <>
                                <Show below="2xl">
                                  <FormLabel m={0}>
                                    {new Intl.DisplayNames(['en'], {
                                      type: 'language',
                                    }).of(lang)}
                                  </FormLabel>
                                </Show>

                                <Box
                                  as={Link}
                                  onClick={() => setEditingLang(null)}
                                  cursor="pointer"
                                />
                                <ChevronUpIcon
                                  color="secondaryDark"
                                  boxSize="20px"
                                />
                              </>
                            </Flex>
                          </Box>
                        </Flex>
                      </FormControl>
                      <Flex
                        direction="column"
                        onClick={() => setEditingLang(lang)}
                      >
                        {getActiveTranslationFields.includes('headerText') && (
                          <FormControl
                            bg={modeFormControlBg}
                            isRequired
                            isInvalid={translationErrors[lang]?.headerText}
                          >
                            <Flex alignItems="center">
                              <Hide below="2xl">
                                <Box pl="24px" flexShrink={0} w="240px">
                                  <FormLabel m={0}>Header text</FormLabel>
                                </Box>
                              </Hide>
                              <Box p="14px" w="full" bg={modeLabelBg}>
                                <Show below="2xl">
                                  <FormLabel>Header text</FormLabel>
                                </Show>
                                <Input
                                  value={
                                    passDownloadCtx.translations[lang]
                                      ?.headerText
                                  }
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    passDownloadCtx.updateTranslations(
                                      lang,
                                      'headerText',
                                      value
                                    );

                                    setTranslationErrors((prev) => ({
                                      ...prev,
                                      [lang]: {
                                        ...prev[lang],
                                        headerText: !value.trim(),
                                      },
                                    }));
                                  }}
                                  variant="filledForDarkBg"
                                  id="headerText"
                                  type="text"
                                  placeholder="Type translation here..."
                                />
                              </Box>
                            </Flex>
                          </FormControl>
                        )}
                        {getActiveTranslationFields.includes(
                          'passDownloadText'
                        ) && (
                          <FormControl
                            bg={modeFormControlBg}
                            isRequired
                            isInvalid={
                              translationErrors[lang]?.passDownloadText
                            }
                          >
                            <Flex alignItems="center">
                              <Hide below="2xl">
                                <Box pl="24px" flexShrink={0} w="240px">
                                  <Flex
                                    direction="row"
                                    align="center"
                                    gap={1}
                                    my={0.5}
                                  >
                                    <FormLabel m={0}>Body text</FormLabel>
                                  </Flex>
                                </Box>
                              </Hide>
                              <Box p="14px" w="full" bg={modeLabelBg}>
                                <Show below="2xl">
                                  <Flex
                                    direction="row"
                                    align="center"
                                    gap={1}
                                    my={0.5}
                                  >
                                    <FormLabel m={0}>Body text</FormLabel>
                                  </Flex>
                                </Show>
                                <Input
                                  value={
                                    passDownloadCtx.translations[lang]
                                      ?.passDownloadText
                                  }
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    passDownloadCtx.updateTranslations(
                                      lang,
                                      'passDownloadText',
                                      value
                                    );

                                    setTranslationErrors((prev) => ({
                                      ...prev,
                                      [lang]: {
                                        ...prev[lang],
                                        passDownloadText: !value.trim(),
                                      },
                                    }));
                                  }}
                                  variant="filledForDarkBg"
                                  id="bodyText"
                                  type="text"
                                  placeholder="Type translation here..."
                                />
                              </Box>
                            </Flex>
                          </FormControl>
                        )}
                        <FormControl
                          bg={modeFormControlBg}
                          isRequired
                          isInvalid={translationErrors[lang]?.successMessage}
                        >
                          <Flex alignItems="center">
                            <Hide below="2xl">
                              <Box pl="24px" flexShrink={0} w="240px">
                                <Flex
                                  direction="row"
                                  align="center"
                                  gap={1}
                                  my={0.5}
                                >
                                  <FormLabel m={0}>
                                    Success screen message
                                  </FormLabel>
                                </Flex>
                              </Box>
                            </Hide>
                            <Box p="14px" w="full" bg={modeLabelBg}>
                              <Show below="2xl">
                                <Flex
                                  direction="row"
                                  align="center"
                                  gap={1}
                                  my={0.5}
                                >
                                  <FormLabel m={0}>
                                    Success screen message
                                  </FormLabel>
                                </Flex>
                              </Show>
                              <Flex
                                direction="row"
                                gap={3}
                                alignItems="center"
                                py={1.5}
                              >
                                <Textarea
                                  id="successMessage"
                                  value={
                                    passDownloadCtx.translations[lang]
                                      ?.successMessage
                                  }
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    passDownloadCtx.updateTranslations(
                                      lang,
                                      'successMessage',
                                      value
                                    );

                                    setTranslationErrors((prev) => ({
                                      ...prev,
                                      [lang]: {
                                        ...prev[lang],
                                        successMessage: !value.trim(),
                                      },
                                    }));
                                  }}
                                  type="text"
                                  placeholder="Type translation here..."
                                />
                              </Flex>
                            </Box>
                          </Flex>
                        </FormControl>
                        {showHelpAndFAQs && (
                          <FormControl
                            bg={modeFormControlBg}
                            isRequired={showHelpAndFAQs}
                            isInvalid={translationErrors[lang]?.clientHelpLink}
                          >
                            <Flex alignItems="center">
                              <Hide below="2xl">
                                <Box pl="24px" flexShrink={0} w="240px">
                                  <Flex
                                    direction="row"
                                    align="center"
                                    gap={1}
                                    my={0.5}
                                  >
                                    <FormLabel m={0}>
                                      Help and FAQs text
                                    </FormLabel>
                                  </Flex>
                                </Box>
                              </Hide>
                              <Box p="14px" w="full" bg={modeLabelBg}>
                                <Show below="2xl">
                                  <Flex
                                    direction="row"
                                    align="center"
                                    gap={1}
                                    my={0.5}
                                  >
                                    <FormLabel m={0}>
                                      Help and FAQs text
                                    </FormLabel>
                                  </Flex>
                                </Show>
                                <Flex
                                  direction="row"
                                  gap={3}
                                  alignItems="center"
                                  py={1.5}
                                >
                                  <Input
                                    value={
                                      passDownloadCtx.translations[lang]
                                        ?.clientHelpLink
                                    }
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      passDownloadCtx.updateTranslations(
                                        lang,
                                        'clientHelpLink',
                                        value
                                      );

                                      setTranslationErrors((prev) => ({
                                        ...prev,
                                        [lang]: {
                                          ...prev[lang],
                                          clientHelpLink: !value.trim(),
                                        },
                                      }));
                                    }}
                                    variant="filledForDarkBg"
                                    id="clientHelpLink"
                                    type="text"
                                    placeholder="Type translation here..."
                                  />
                                </Flex>
                              </Box>
                            </Flex>
                          </FormControl>
                        )}
                        <FormControl
                          bg={modeFormControlBg}
                          isRequired
                          isInvalid={translationErrors[lang]?.pageTitle}
                        >
                          <Flex alignItems="center">
                            <Hide below="2xl">
                              <Box pl="24px" flexShrink={0} w="240px">
                                <FormLabel m={0}>Browser tab text</FormLabel>
                              </Box>
                            </Hide>
                            <Box p="14px" w="full" bg={modeLabelBg}>
                              <Show below="2xl">
                                <FormLabel>Browser tab text</FormLabel>
                              </Show>
                              <Input
                                value={
                                  passDownloadCtx.translations[lang]?.pageTitle
                                }
                                onChange={(e) => {
                                  const value = e.target.value;
                                  passDownloadCtx.updateTranslations(
                                    lang,
                                    'pageTitle',
                                    value
                                  );

                                  setTranslationErrors((prev) => ({
                                    ...prev,
                                    [lang]: {
                                      ...prev[lang],
                                      pageTitle: !value.trim(),
                                    },
                                  }));
                                }}
                                variant="filledForDarkBg"
                                id="pageTitle"
                                type="text"
                                placeholder="Type translation here..."
                              />
                            </Box>
                          </Flex>
                        </FormControl>
                        <FormControl
                          bg={modeFormControlBg}
                          borderRadius={
                            index === langCodes.length - 1 ? '15px' : '0'
                          }
                        >
                          <Flex alignItems="center">
                            <Hide below="2xl">
                              <Box
                                pl="24px"
                                flexShrink={0}
                                w="240px"
                                borderRadius={
                                  index === langCodes.length - 1 ? '15px' : '0'
                                }
                              />
                            </Hide>
                            <Box
                              p="10px 0px 20px 24px"
                              w="full"
                              bg={modeLabelBg}
                              borderRadius={
                                index === langCodes.length - 1
                                  ? {
                                      base: '0 0 15px 15px',
                                      '2xl': '0 0 15px 0',
                                    }
                                  : '0'
                              }
                            >
                              <Show below="2xl" />
                              <Button
                                size="xs"
                                variant="outline"
                                color="red"
                                borderRadius="5px"
                                borderColor="red"
                                borderWidth="1.5px"
                                fontSize="12px"
                                _hover={{
                                  borderColor: 'red.400',
                                  color: 'red.400',
                                }}
                                onClick={() => {
                                  setSelectedLanguage(lang);
                                  setIsRemoveLanguageModalOpen(true);
                                }}
                              >
                                Remove language
                              </Button>
                            </Box>
                          </Flex>
                        </FormControl>
                        <Divider borderColor={modeBorderColor} />
                      </Flex>
                    </>
                  )}
                </>
              ))}
            </Collapse>
            <AddLanguageModal
              isOpen={isAddLanguageModalOpen}
              onClose={() => setIsAddLanguageModalOpen(false)}
              setEditingLang={setEditingLang}
              selectedLanguage={selectedLanguage}
              setSelectedLanguage={setSelectedLanguage}
              languageOptions={languageOptions}
            />
            <RemoveLanguageModal
              isOpen={isRemoveLanguageModalOpen}
              onClose={() => setIsRemoveLanguageModalOpen(false)}
              selectedLanguage={selectedLanguage}
              setEditingLang={setEditingLang}
            />
          </Box>
        </Box>
      )}
      <Box
        className="autofillForDarkBg"
        borderRadius="15px"
        w="full"
        mt="20px"
        onClick={() => setEditingLang(null)}
        border="0.5px solid #D9D9D9"
        boxShadow="0px 5px 10px rgba(0, 0, 0, 0.05)"
        overflow="hidden"
      >
        <Box bg={modeFormBoxBg} w="full" borderRadius="15px">
          <Stack
            p="14px 14px 14px 24px"
            bg={modeTextBg}
            direction="row"
            align="center"
            borderRadius="15px 15px 0 0"
          >
            <Flex direction="row" gap={1} align="center">
              <Text fontSize="18px" textStyle="headingFamilyMedium">
                Design
              </Text>
            </Flex>
          </Stack>
          <Divider borderColor={modeBorderColor} />
          <FormControl bg={modeFormControlBg}>
            <Flex alignItems="center">
              <Hide below="2xl">
                <Box pl="24px" flexShrink={0} w="240px">
                  <Flex direction="row" align="center" gap={1} my={0.5}>
                    <FormLabel m={0}> Skip "Add to Wallet" screen</FormLabel>
                    <Tooltip
                      label="Skipping this screen will automatically trigger the pass download on a user's device"
                      hasArrow
                      placement="top"
                      fontSize="12px"
                      bg={bgTooltip}
                      color={textTooltip}
                      w="205px"
                      borderRadius="6px"
                      textAlign="center"
                      p="10px"
                    >
                      <Box
                        data-testid="skip-add-to-wallet-tooltip-button"
                        mb={1}
                        cursor="pointer"
                      >
                        <CustomExclamationMarkInverseIcon
                          boxSize="12px"
                          mode={'light'}
                        />
                      </Box>
                    </Tooltip>
                  </Flex>
                </Box>
              </Hide>
              <Box p="14px" w="full" bg={modeLabelBg}>
                <Show below="2xl">
                  <Flex direction="row" align="center" gap={1} my={0.5}>
                    <FormLabel m={0}> Skip "Add to Wallet" screen</FormLabel>
                    <Tooltip
                      label="Skipping this screen will automatically trigger the pass download on a user's device"
                      hasArrow
                      placement="top"
                      fontSize="12px"
                      bg={bgTooltip}
                      color={textTooltip}
                      w="205px"
                      borderRadius="6px"
                      textAlign="center"
                      p="10px"
                    >
                      <Box
                        data-testid="skip-add-to-wallet-tooltip-button"
                        mb={1}
                        cursor="pointer"
                      >
                        <CustomExclamationMarkInverseIcon
                          boxSize="12px"
                          mode={'light'}
                        />
                      </Box>
                    </Tooltip>
                  </Flex>
                </Show>
                <Flex
                  direction="row"
                  gap={3}
                  alignItems="center"
                  ml={3}
                  py={1.5}
                >
                  <Switch
                    data-testid="skip-add-to-wallet-switch"
                    role="switch"
                    aria-checked={passDownloadCtx.skipPreview}
                    id="skipPreview"
                    isChecked={passDownloadCtx.skipPreview}
                    onChange={() => {
                      passDownloadCtx.updateSkipPreview(
                        !passDownloadCtx.skipPreview
                      );
                    }}
                  />
                </Flex>
              </Box>
            </Flex>
          </FormControl>
          <Divider />
          <FormControl isRequired bg={modeFormControlBg}>
            <Flex data-testid="logo-image">
              <Hide below="2xl">
                <Box pl="24px" flexShrink={0} w="240px">
                  <FormLabel m={0} pt="18px">
                    Logo image
                  </FormLabel>
                </Box>
              </Hide>
              <Box p="14px" w="full" bg={modeLabelBg}>
                <Show below="2xl">
                  <FormLabel>Logo image</FormLabel>
                </Show>
                <LogoImage
                  image={passDownloadCtx.logo}
                  setImage={(image) => passDownloadCtx.updateLogo(image)}
                  setLogo={(image) => passDownloadCtx.updateLogoImage(image)}
                  parent="download_page"
                />
              </Box>
            </Flex>
          </FormControl>
          <Divider />
          <FormControl isRequired bg={modeFormControlBg}>
            <Flex>
              <Hide below="2xl">
                <Box pl="24px" flexShrink={0} w="240px">
                  <FormLabel m={0} pt="18px">
                    Favicon
                  </FormLabel>
                </Box>
              </Hide>
              <Box p="14px" w="full" bg={modeLabelBg}>
                <Show below="2xl">
                  <FormLabel>Favicon</FormLabel>
                </Show>
                <FaviconImage
                  faviconImage={passDownloadCtx.faviconLogo}
                  setFaviconImage={(faviconImage) =>
                    passDownloadCtx.updateFaviconImage(faviconImage)
                  }
                  setFaviconLogo={(faviconLogo) =>
                    passDownloadCtx.updateFaviconLogo(faviconLogo)
                  }
                />
              </Box>
            </Flex>
          </FormControl>
          <Divider />
          <FormControl bg={modeFormControlBg}>
            <Flex alignItems="center">
              <Hide below="2xl">
                <Box pl="24px" flexShrink={0} w="240px">
                  <FormLabel m={0}>Colours</FormLabel>
                </Box>
              </Hide>
              <Box p="14px" w="full" bg={modeLabelBg}>
                <Show below="2xl">
                  <FormLabel>Colours</FormLabel>
                </Show>
                <Grid templateColumns="repeat(2, 1fr)">
                  <GridItem ml="16px">
                    <FormControl>
                      <Flex direction="column">
                        <Box p="10px 10px 5px 0px" w="full" bg={modeLabelBg}>
                          <CustomColorPicker
                            parent="download_page"
                            color={passDownloadCtx.backgroundColor}
                            onChange={(color) =>
                              passDownloadCtx.updateBackgroundColor(color)
                            }
                            hexError={false}
                          />
                          <Show below="2xl">
                            <FormLabel color="#5F6971">Background</FormLabel>
                          </Show>
                        </Box>
                        <Hide below="2xl">
                          <Box flexShrink={0} w="240px">
                            <FormLabel color="#5F6971" m={0}>
                              Background
                            </FormLabel>
                          </Box>
                        </Hide>
                      </Flex>
                    </FormControl>
                    <FormControl mt={3}>
                      <Flex direction="column">
                        <Box p="10px 10px 5px 0px" w="full" bg={modeLabelBg}>
                          <CustomColorPicker
                            parent="download_page"
                            color={passDownloadCtx.headerColor}
                            onChange={(color) =>
                              passDownloadCtx.updateHeaderColor(color)
                            }
                            hexError={false}
                          />
                          <Show below="2xl">
                            <FormLabel color="#5F6971">Header text</FormLabel>
                          </Show>
                        </Box>
                        <Hide below="2xl">
                          <Box flexShrink={0} w="240px">
                            <FormLabel color="#5F6971" m={0}>
                              Header text
                            </FormLabel>
                          </Box>
                        </Hide>
                      </Flex>
                    </FormControl>
                    <FormControl mt={3}>
                      <Flex direction="column">
                        <Box p="10px 10px 5px 0px" w="full" bg={modeLabelBg}>
                          <CustomColorPicker
                            parent="download_page"
                            color={passDownloadCtx.passDownloadTextColor}
                            onChange={(color) =>
                              passDownloadCtx.updatePassDownloadTextColor(color)
                            }
                            hexError={false}
                          />
                          <Show below="2xl">
                            <FormLabel color="#5F6971">Body text</FormLabel>
                          </Show>
                        </Box>
                        <Hide below="2xl">
                          <Box flexShrink={0} w="240px">
                            <FormLabel color="#5F6971" m={0}>
                              Body text
                            </FormLabel>
                          </Box>
                        </Hide>
                      </Flex>
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>
            </Flex>
          </FormControl>
          <Divider />
          <FormControl bg={modeFormControlBg} borderRadius="15px">
            <Flex alignItems="center">
              <Hide below="2xl">
                <Box
                  pl="16px"
                  flexShrink={0}
                  w="240px"
                  borderRadius="0 0 0 15px"
                >
                  <Flex direction="row" align="center" gap={1}>
                    <FormLabel m={0}>Component colours</FormLabel>
                    <Tooltip
                      label="Includes interactive elements such as the loading spinner, buttons, and links"
                      hasArrow
                      placement="top"
                      fontSize="12px"
                      bg={bgTooltip}
                      color={textTooltip}
                      w="205px"
                      borderRadius="6px"
                      textAlign="center"
                      p="10px"
                    >
                      <Box
                        data-testid="interactive-ui-components-tooltip-button"
                        mb={1}
                        cursor="pointer"
                      >
                        <CustomExclamationMarkInverseIcon
                          boxSize="12px"
                          mode={'light'}
                        />
                      </Box>
                    </Tooltip>
                  </Flex>
                </Box>
              </Hide>
              <Box
                p="14px"
                w="full"
                bg={modeLabelBg}
                borderRadius={{ base: '0 0 15px 15px', '2xl': '0 0 15px 0' }}
              >
                <Show below="2xl">
                  <Flex direction="row" alignItems="center" gap={1}>
                    <FormLabel m={0}>Component colours</FormLabel>
                    <Tooltip
                      label="Includes interactive elements such as the loading spinner, buttons, and links"
                      hasArrow
                      placement="top"
                      fontSize="12px"
                      bg={bgTooltip}
                      color={textTooltip}
                      w="205px"
                      borderRadius="6px"
                      textAlign="center"
                      p="10px"
                    >
                      <Box
                        data-testid="interactive-ui-components-tooltip-button"
                        mb={1}
                        cursor="pointer"
                      >
                        <CustomExclamationMarkInverseIcon
                          boxSize="14px"
                          mode={'light'}
                        />
                      </Box>
                    </Tooltip>
                  </Flex>
                </Show>
                <Grid templateColumns="repeat(2, 1fr)">
                  <GridItem ml="16px">
                    <FormControl>
                      <Flex direction="column">
                        <Box p="10px 10px 5px 0px" w="full" bg={modeLabelBg}>
                          <CustomColorPicker
                            parent="download_page"
                            color={passDownloadCtx.componentFillColor}
                            onChange={(color) =>
                              passDownloadCtx.updateComponentFillColor(color)
                            }
                            hexError={false}
                          />
                          <Show below="2xl">
                            <FormLabel color="#5F6971">Fill</FormLabel>
                          </Show>
                        </Box>
                        <Hide below="2xl">
                          <Box flexShrink={0} w="240px">
                            <FormLabel color="#5F6971" m={0}>
                              Fill
                            </FormLabel>
                          </Box>
                        </Hide>
                      </Flex>
                    </FormControl>
                    <FormControl mt={3}>
                      <Flex direction="column">
                        <Box p="10px 10px 5px 0px" w="full" bg={modeLabelBg}>
                          <CustomColorPicker
                            parent="download_page"
                            color={passDownloadCtx.componentFontColor}
                            onChange={(color) =>
                              passDownloadCtx.updateComponentFontColor(color)
                            }
                            hexError={false}
                          />
                          <Show below="2xl">
                            <FormLabel color="#5F6971">Text</FormLabel>
                          </Show>
                        </Box>
                        <Hide below="2xl">
                          <Box flexShrink={0} w="240px">
                            <FormLabel color="#5F6971" m={0}>
                              Text
                            </FormLabel>
                          </Box>
                        </Hide>
                      </Flex>
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>
            </Flex>
          </FormControl>
        </Box>
      </Box>
      <Box
        className="autofillForDarkBg"
        borderRadius="15px"
        overflow="hidden"
        w="full"
        mt="20px"
        onClick={() => setEditingLang(null)}
        border="0.5px solid #D9D9D9"
        boxShadow="0px 5px 10px rgba(0, 0, 0, 0.05)"
        overflow="hidden"
      >
        <Box bg={modeFormBoxBg} w="full">
          <Stack
            p="14px 14px 14px 24px"
            bg={modeTextBg}
            direction="row"
            align="center"
          >
            <Flex direction="row" gap={1} align="center">
              <Text fontSize="18px" textStyle="headingFamilyMedium">
                Links
              </Text>
            </Flex>
          </Stack>
          <FormControl bg={modeFormControlBg}>
            <Flex alignItems="center">
              <Hide below="2xl">
                <Box pl="24px" flexShrink={0} w="240px">
                  <Flex direction="row" align="center" gap={1} my={0.5}>
                    <FormLabel m={0}> Display help and FAQs link</FormLabel>
                    <Tooltip
                      label="Option to display a link to troubleshooting information on the pass download page"
                      hasArrow
                      placement="top"
                      fontSize="12px"
                      bg={bgTooltip}
                      color={textTooltip}
                      w="205px"
                      borderRadius="6px"
                      textAlign="center"
                      p="10px"
                    >
                      <Box
                        data-testid="display-help-and-faqs-tooltip-button"
                        mb={1}
                        cursor="pointer"
                      >
                        <CustomExclamationMarkInverseIcon
                          boxSize="12px"
                          mode={'light'}
                        />
                      </Box>
                    </Tooltip>
                  </Flex>
                </Box>
              </Hide>
              <Box p="14px" w="full" bg={modeLabelBg}>
                <Show below="2xl">
                  <Flex direction="row" align="center" gap={1} my={0.5}>
                    <FormLabel m={0}>Display help and FAQs link</FormLabel>
                    <Tooltip
                      label="Option to display a link to troubleshooting information on the pass download page"
                      hasArrow
                      placement="top"
                      fontSize="12px"
                      bg={bgTooltip}
                      color={textTooltip}
                      w="205px"
                      borderRadius="6px"
                      textAlign="center"
                      p="10px"
                    >
                      <Box
                        data-testid="display-help-and-faqs-tooltip-button"
                        mb={1}
                        cursor="pointer"
                      >
                        <CustomExclamationMarkInverseIcon
                          boxSize="12px"
                          mode={'light'}
                        />
                      </Box>
                    </Tooltip>
                  </Flex>
                </Show>
                <Flex
                  direction="row"
                  gap={3}
                  alignItems="center"
                  ml={3}
                  py={1.5}
                >
                  <Switch
                    data-testid="display-help-and-faqs-switch"
                    role="switch"
                    aria-checked={showHelpAndFAQs}
                    id="showHelpAndFAQs"
                    isChecked={showHelpAndFAQs}
                    onChange={() => {
                      setShowHelpAndFAQs(!showHelpAndFAQs);
                    }}
                  />
                </Flex>
              </Box>
            </Flex>
          </FormControl>
          <Divider />
          <Collapse in={showHelpAndFAQs} animateOpacity>
            <FormControl bg={modeFormControlBg}>
              <Flex alignItems="center">
                <Hide below="2xl">
                  <Box pl="24px" flexShrink={0} w="240px">
                    <FormLabel m={0}>Help and FAQs text</FormLabel>
                  </Box>
                </Hide>
                <Box p="14px" w="full" bg={modeLabelBg}>
                  <Show below="2xl">
                    <FormLabel>Help and FAQs text</FormLabel>
                  </Show>
                  <Input
                    value={passDownloadCtx.clientHelpLink?.display_text}
                    onChange={(e) =>
                      passDownloadCtx.updateClientHelpLink({
                        ...passDownloadCtx.clientHelpLink,
                        display_text: e.target.value,
                      })
                    }
                    variant="filledForDarkBg"
                    id="websiteUrl"
                    type="text"
                    placeholder="Type link text here..."
                  />
                </Box>
              </Flex>
            </FormControl>
            <Divider />
            <FormControl bg={modeFormControlBg}>
              <Flex alignItems="center">
                <Hide below="2xl">
                  <Box pl="24px" flexShrink={0} w="240px">
                    <FormLabel m={0}>Help and FAQs URL</FormLabel>
                  </Box>
                </Hide>
                <Box p="14px" w="full" bg={modeLabelBg}>
                  <Show below="2xl">
                    <FormLabel>Help and FAQs URL</FormLabel>
                  </Show>
                  <Input
                    value={passDownloadCtx.clientHelpLink?.url}
                    onChange={(e) =>
                      passDownloadCtx.updateClientHelpLink({
                        ...passDownloadCtx.clientHelpLink,
                        url: e.target.value,
                      })
                    }
                    variant="filledForDarkBg"
                    id="websiteUrl"
                    type="text"
                    placeholder="Type link URL here..."
                  />
                </Box>
              </Flex>
            </FormControl>
            <Divider />
          </Collapse>
          <FormControl bg={modeFormControlBg}>
            <Flex alignItems="center">
              <Hide below="2xl">
                <Box pl="24px" flexShrink={0} w="240px">
                  <FormLabel m={0}>Website URL</FormLabel>
                  <Text
                    color={modeHelperText}
                    textStyle="bodyFamilyRegular"
                    fontSize="12px"
                  >
                    Linked to logo image
                  </Text>
                </Box>
              </Hide>
              <Box p="14px" w="full" bg={modeLabelBg}>
                <Show below="2xl">
                  <FormLabel m={0}>Website URL</FormLabel>
                  <Text
                    color={modeHelperText}
                    textStyle="bodyFamilyRegular"
                    fontSize="12px"
                    m="0 0 14px 0"
                  >
                    Linked to logo image
                  </Text>
                </Show>
                <Input
                  value={passDownloadCtx.clientWebsite}
                  onChange={(e) =>
                    passDownloadCtx.updateClientWebsite(e.target.value)
                  }
                  variant="filledForDarkBg"
                  id="websiteUrl"
                  type="text"
                  placeholder="Type website URL here..."
                />
              </Box>
            </Flex>
          </FormControl>
          <Divider />
        </Box>
      </Box>
    </Box>
  );
};

export default PassDownloadTemplateDetails;
